import React, { useState } from 'react'
import { Copy, BrainCircuit, Database, Info, Lightbulb, Gem, ChevronDown, ChevronRight } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'

interface DataExtraction {
  rawFacts: string[]
  numbers: string[]
  dates: string[]
  names: string[]
  locations: string[]
  quantities: string[]
  categories: string[]
  sources: string[]
  metadata: {
    extractionConfidence: number
    totalDataPoints: number
    dataQuality: 'high' | 'medium' | 'low'
  }
}

interface InformationExtraction {
  processedData: string[]
  contexts: string[]
  relationships: string[]
  patterns: string[]
  trends: string[]
  summaries: string[]
  classifications: string[]
  interpretations: string[]
  metadata: {
    contextualRelevance: number
    informationDensity: 'high' | 'medium' | 'low'
    clarity: number
  }
}

interface KnowledgeExtraction {
  insights: string[]
  principles: string[]
  bestPractices: string[]
  lessons: string[]
  frameworks: string[]
  methodologies: string[]
  experiences: string[]
  expertise: string[]
  metadata: {
    knowledgeDepth: number
    applicability: 'universal' | 'domain-specific' | 'context-specific'
    evidenceStrength: 'strong' | 'moderate' | 'weak'
  }
}

interface WisdomExtraction {
  judgments: string[]
  recommendations: string[]
  strategicInsights: string[]
  ethicalConsiderations: string[]
  longTermImplications: string[]
  balancedPerspectives: string[]
  cautionaryAdvice: string[]
  paradoxes: string[]
  metadata: {
    wisdomLevel: number
    practicalValue: 'high' | 'medium' | 'low'
    universalApplicability: number
  }
}

interface DIKWData {
  data: DataExtraction
  information: InformationExtraction
  knowledge: KnowledgeExtraction
  wisdom: WisdomExtraction
}

interface DIKWDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  dikwData: DIKWData | null
  onClose: () => void
}

export const DIKWDialog: React.FC<DIKWDialogProps> = ({
  open,
  onOpenChange,
  dikwData,
  onClose,
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    data: true,
    information: true,
    knowledge: true,
    wisdom: true,
  })

  if (!dikwData) {
    return null
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const getQualityColor = (quality: string) => {
    switch (quality.toLowerCase()) {
      case 'high':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getApplicabilityColor = (applicability: string) => {
    switch (applicability) {
      case 'universal':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'domain-specific':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'context-specific':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {
      console.error('Failed to copy to clipboard')
    })
  }

  const copyAllDIKW = () => {
    const allContent = `
# DIKW Extraction Results

## Data (Raw Facts)
${dikwData.data.rawFacts.map(fact => `• ${fact}`).join('\n')}

**Numbers:** ${dikwData.data.numbers.join(', ')}
**Dates:** ${dikwData.data.dates.join(', ')}
**Names:** ${dikwData.data.names.join(', ')}
**Locations:** ${dikwData.data.locations.join(', ')}

## Information (Processed Data)
${dikwData.information.processedData.map(info => `• ${info}`).join('\n')}

**Relationships:** ${dikwData.information.relationships.join(', ')}
**Patterns:** ${dikwData.information.patterns.join(', ')}

## Knowledge (Insights & Principles)
${dikwData.knowledge.insights.map(insight => `• ${insight}`).join('\n')}

**Best Practices:** ${dikwData.knowledge.bestPractices.join(', ')}
**Frameworks:** ${dikwData.knowledge.frameworks.join(', ')}

## Wisdom (Strategic Insights)
${dikwData.wisdom.strategicInsights.map(wisdom => `• ${wisdom}`).join('\n')}

**Recommendations:** ${dikwData.wisdom.recommendations.join(', ')}
**Ethical Considerations:** ${dikwData.wisdom.ethicalConsiderations.join(', ')}
`
    copyToClipboard(allContent)
  }

  const renderArrayItems = (items: string[], title: string) => {
    if (items.length === 0) {
      return (
        <div className="text-sm text-gray-500 italic">
          No {title.toLowerCase()} extracted
        </div>
      )
    }

    return (
      <div className="space-y-1">
        {items.map((item, index) => (
          <div key={index} className="flex items-start gap-2 p-2 bg-gray-50 rounded-md text-sm">
            <span className="text-gray-600 font-mono text-xs mt-0.5">
              {(index + 1).toString().padStart(2, '0')}
            </span>
            <span className="flex-1">{item}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard(item)}
              className="h-6 w-6 p-0 opacity-50 hover:opacity-100"
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
    )
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent 
        className="max-w-7xl max-h-[90vh] w-[95vw]" 
        style={{ width: '95vw', maxWidth: '1400px' }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <BrainCircuit className="h-5 w-5 text-purple-600" />
            DIKW Knowledge Extraction
          </AlertDialogTitle>
          <AlertDialogDescription>
            Data, Information, Knowledge, and Wisdom layers extracted from your content
          </AlertDialogDescription>
        </AlertDialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Data Layer */}
            <Card>              <Collapsible
                open={expandedSections.data}
                onOpenChange={() => toggleSection('data')}
              >
                <CollapsibleTrigger asChild>
                  <CardHeader hasCardAction className="cursor-pointer hover:bg-gray-50 transition-colors">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Database className="h-5 w-5 text-blue-600" />
                        Data Layer
                        <Badge className={getQualityColor(dikwData.data.metadata.dataQuality)}>
                          {dikwData.data.metadata.dataQuality} quality
                        </Badge>
                        <Badge variant="outline">
                          {dikwData.data.metadata.totalDataPoints} points
                        </Badge>
                        <Badge variant="outline">
                          {dikwData.data.metadata.extractionConfidence}% confidence
                        </Badge>
                      </div>
                      {expandedSections.data ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </CardTitle>
                    <CardDescription>
                      Raw facts, figures, and observable elements
                    </CardDescription>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Raw Facts</h4>
                        {renderArrayItems(dikwData.data.rawFacts, 'facts')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Numbers & Quantities</h4>
                        {renderArrayItems([...dikwData.data.numbers, ...dikwData.data.quantities], 'numbers')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Names & Entities</h4>
                        {renderArrayItems(dikwData.data.names, 'names')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Dates & Locations</h4>
                        {renderArrayItems([...dikwData.data.dates, ...dikwData.data.locations], 'dates and locations')}
                      </div>
                    </div>
                    {dikwData.data.categories.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Categories</h4>
                        {renderArrayItems(dikwData.data.categories, 'categories')}
                      </div>
                    )}
                    {dikwData.data.sources.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Sources</h4>
                        {renderArrayItems(dikwData.data.sources, 'sources')}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>

            {/* Information Layer */}
            <Card>
              <Collapsible
                open={expandedSections.information}
                onOpenChange={() => toggleSection('information')}
              >                <CollapsibleTrigger asChild>
                  <CardHeader hasCardAction className="cursor-pointer hover:bg-gray-50 transition-colors">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Info className="h-5 w-5 text-green-600" />
                        Information Layer
                        <Badge className={getQualityColor(dikwData.information.metadata.informationDensity)}>
                          {dikwData.information.metadata.informationDensity} density
                        </Badge>
                        <Badge variant="outline">
                          {dikwData.information.metadata.contextualRelevance}% relevance
                        </Badge>
                        <Badge variant="outline">
                          {dikwData.information.metadata.clarity}% clarity
                        </Badge>
                      </div>
                      {expandedSections.information ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </CardTitle>
                    <CardDescription>
                      Processed data with context, patterns, and relationships
                    </CardDescription>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Processed Data</h4>
                        {renderArrayItems(dikwData.information.processedData, 'processed data')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Contexts</h4>
                        {renderArrayItems(dikwData.information.contexts, 'contexts')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Relationships</h4>
                        {renderArrayItems(dikwData.information.relationships, 'relationships')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Patterns & Trends</h4>
                        {renderArrayItems([...dikwData.information.patterns, ...dikwData.information.trends], 'patterns')}
                      </div>
                    </div>
                    {dikwData.information.summaries.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Summaries</h4>
                        {renderArrayItems(dikwData.information.summaries, 'summaries')}
                      </div>
                    )}
                    {dikwData.information.interpretations.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Interpretations</h4>
                        {renderArrayItems(dikwData.information.interpretations, 'interpretations')}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>

            {/* Knowledge Layer */}
            <Card>
              <Collapsible
                open={expandedSections.knowledge}
                onOpenChange={() => toggleSection('knowledge')}
              >                <CollapsibleTrigger asChild>
                  <CardHeader hasCardAction className="cursor-pointer hover:bg-gray-50 transition-colors">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="h-5 w-5 text-yellow-600" />
                        Knowledge Layer
                        <Badge className={getApplicabilityColor(dikwData.knowledge.metadata.applicability)}>
                          {dikwData.knowledge.metadata.applicability}
                        </Badge>
                        <Badge variant="outline">
                          {dikwData.knowledge.metadata.knowledgeDepth}% depth
                        </Badge>
                        <Badge variant="outline">
                          {dikwData.knowledge.metadata.evidenceStrength} evidence
                        </Badge>
                      </div>
                      {expandedSections.knowledge ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </CardTitle>
                    <CardDescription>
                      Insights, principles, and understanding from experience
                    </CardDescription>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Insights</h4>
                        {renderArrayItems(dikwData.knowledge.insights, 'insights')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Principles</h4>
                        {renderArrayItems(dikwData.knowledge.principles, 'principles')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Best Practices</h4>
                        {renderArrayItems(dikwData.knowledge.bestPractices, 'best practices')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Lessons</h4>
                        {renderArrayItems(dikwData.knowledge.lessons, 'lessons')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Frameworks</h4>
                        {renderArrayItems(dikwData.knowledge.frameworks, 'frameworks')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Methodologies</h4>
                        {renderArrayItems(dikwData.knowledge.methodologies, 'methodologies')}
                      </div>
                    </div>
                    {dikwData.knowledge.experiences.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Experiences</h4>
                        {renderArrayItems(dikwData.knowledge.experiences, 'experiences')}
                      </div>
                    )}
                    {dikwData.knowledge.expertise.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Expertise</h4>
                        {renderArrayItems(dikwData.knowledge.expertise, 'expertise')}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>

            {/* Wisdom Layer */}
            <Card>
              <Collapsible
                open={expandedSections.wisdom}
                onOpenChange={() => toggleSection('wisdom')}
              >                <CollapsibleTrigger asChild>
                  <CardHeader hasCardAction className="cursor-pointer hover:bg-gray-50 transition-colors">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Gem className="h-5 w-5 text-purple-600" />
                        Wisdom Layer
                        <Badge className={getQualityColor(dikwData.wisdom.metadata.practicalValue)}>
                          {dikwData.wisdom.metadata.practicalValue} value
                        </Badge>
                        <Badge variant="outline">
                          {dikwData.wisdom.metadata.wisdomLevel}% wisdom
                        </Badge>
                        <Badge variant="outline">
                          {dikwData.wisdom.metadata.universalApplicability}% universal
                        </Badge>
                      </div>
                      {expandedSections.wisdom ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </CardTitle>
                    <CardDescription>
                      Strategic insights, judgment, and ethical considerations
                    </CardDescription>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Strategic Insights</h4>
                        {renderArrayItems(dikwData.wisdom.strategicInsights, 'strategic insights')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Judgments</h4>
                        {renderArrayItems(dikwData.wisdom.judgments, 'judgments')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Recommendations</h4>
                        {renderArrayItems(dikwData.wisdom.recommendations, 'recommendations')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Ethical Considerations</h4>
                        {renderArrayItems(dikwData.wisdom.ethicalConsiderations, 'ethical considerations')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Long-term Implications</h4>
                        {renderArrayItems(dikwData.wisdom.longTermImplications, 'implications')}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Balanced Perspectives</h4>
                        {renderArrayItems(dikwData.wisdom.balancedPerspectives, 'perspectives')}
                      </div>
                    </div>
                    {dikwData.wisdom.cautionaryAdvice.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Cautionary Advice</h4>
                        {renderArrayItems(dikwData.wisdom.cautionaryAdvice, 'cautionary advice')}
                      </div>
                    )}
                    {dikwData.wisdom.paradoxes.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Paradoxes</h4>
                        {renderArrayItems(dikwData.wisdom.paradoxes, 'paradoxes')}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          </div>
        </ScrollArea>

        <Separator />

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={copyAllDIKW}
            className="mr-auto"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy All DIKW
          </Button>
          <AlertDialogCancel onClick={onClose}>Close</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
